"use client";

import { FC, useContext, useEffect, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { useScopedI18n } from "@/i18n/client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { PAGESIZE_KEYWORD } from "@/lib/searchspring/constants";
import { ToApplyFacetData } from "@/lib/searchspring/types";
import { getSortByIdentifier } from "@/lib/searchspring/utils";
import { SearchSpringContext } from "../SearchSpringContext/SearchSpringContextProvider";
import ToggleFilterAccordion from "@/components/ToggleFilterAccordian/ToggleFilterAccordian";
import Button from "@/components/UI/Button/Button";
import ActiveFilters from "./ActiveFilters/ActiveFilters";
import PageSize from "./Sort/PageSize/PageSize";
import SortBy from "./Sort/SortBy/SortBy";
import FilterIcon from "@/app/assets/images/svg/sliders.svg";
import CloseIcon from "@/app/assets/images/svg/xmark.svg";
import styles from "./SortAndFilter.module.scss";

interface Props {
	filters?: any[];
	suggestedCategories?: any[];
}

const SortAndFilter: FC<Props> = ({ filters, suggestedCategories }) => {
	const { facetsToApply, pageSize, sortBy, reset } =
		useContext(SearchSpringContext);
	const t = useScopedI18n("search");

	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();

	const [isOpen, setIsOpen] = useState(false);

	useEffect(() => {
		setIsOpen(false);
	}, [pathname, searchParams]);

	const applyFilters = (filters: ToApplyFacetData[] | undefined) => {
		const params = new URLSearchParams();
		let href = pathname;

		// Reattach the current search query to the href
		searchParams.forEach((value, key) => {
			if (key === "query") {
				params.append(key, value);
			}
		});

		if (sortBy) {
			const [key, value] = getSortByIdentifier(sortBy).split("=");
			params.set(key, value);
		}

		if (pageSize) {
			params.set(PAGESIZE_KEYWORD, String(pageSize));
		}

		for (const entry of filters || []) {
			for (const param of entry.params || []) {
				const [key, value] = param.split("=");
				params.append(key, value);
			}
		}

		const paramsString = params.toString();
		href = `${pathname}${paramsString ? `?${paramsString}` : ""}`;
		router.push(href);
	};

	const handleClose = () => {
		setIsOpen(false);
	};

	return (
		<>
			<Button
				className={styles.button}
				as='button'
				variant='secondary'
				onClick={() => setIsOpen(true)}
			>
				<FilterIcon />
				{sortBy?.label ? sortBy.label : t("filters")}
			</Button>
			<Dialog.Root open={isOpen}>
				<Dialog.Portal>
					<Dialog.Overlay
						className={clsx([
							styles.overlay,
							isOpen ? styles.open : styles.closed,
						])}
						onClick={() => handleClose()}
					/>
					<Dialog.Content
						className={clsx([
							styles.content,
							isOpen ? styles.open : styles.closed,
						])}
					>
						<div className={styles.header}>
							{t("sortAndFilter")}
							<Dialog.Close
								className={styles.close}
								asChild
								onClick={() => handleClose()}
							>
								<CloseIcon />
							</Dialog.Close>
						</div>
						<div className={styles.items}>
							<div className={styles.section}>
								<SortBy />
								<PageSize />
							</div>
							<ToggleFilterAccordion categories={suggestedCategories} />
							<ActiveFilters
								filters={filters}
								clearAllFilters={() => applyFilters(undefined)}
							/>
							{filters?.map((filter) => {
								if (filter.field === "price") return null;
								return (
									<ToggleFilterAccordion key={filter.field} filter={filter} />
								);
							})}
						</div>
						<div className={styles.actions}>
							<Button
								variant={"primary"}
								as='link'
								onClick={() => applyFilters(facetsToApply)}
							>
								{t("showResults")}
							</Button>
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>
		</>
	);
};

export default SortAndFilter;
